import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { chatKeys, saveProposalKeys } from '~/lib/query-keys';
import type { VoiceTurn } from './use-voice-call';
import { describeVoiceRecapOutcome, type EndedResult } from './voice-recap-outcome';

/**
 * End-of-call bookkeeping for a voice call bound to one chat: hand the server
 * the transcript held in memory (it writes the recap as an assistant turn and
 * the Approve/Reject save proposal, both keyed to `sessionId`), tell the
 * person what actually happened, then refetch the chat so the recap and the
 * save card appear.
 *
 * Moved out of the chat window (FRONT-531 / FRONT-581, unchanged) so the
 * hands-free call (FRONT-254) — which outlives the window it started in —
 * runs the exact same path from the app shell.
 */
export async function persistCallEnded(
  queryClient: QueryClient,
  params: { sessionId: string; branchId: string; turns: VoiceTurn[] },
): Promise<void> {
  const { sessionId, branchId, turns } = params;
  // FRONT-581: tell the truth. Show progress while the server generates the
  // recap, then resolve to what actually happened — success only when the
  // recap was persisted, an honest error when it failed.
  const toastId = toast.loading('Saving the call recap…');
  let result: EndedResult | null = null;
  try {
    result = await api.post<EndedResult>('/api/voice/realtime/ended', { sessionId, branchId, turns });
  } catch (err) {
    console.warn('[voice-call] /ended failed:', err);
    result = null;
  }
  const outcome = describeVoiceRecapOutcome(result);
  const opts = {
    id: toastId,
    description: outcome.description,
    duration: outcome.kind === 'success' ? 15000 : undefined,
  };
  if (outcome.kind === 'success') toast.success(outcome.title, opts);
  else if (outcome.kind === 'error') toast.error(outcome.title, opts);
  else toast.info(outcome.title, opts);

  queryClient.invalidateQueries({ queryKey: chatKeys.history(sessionId, branchId) });
  queryClient.invalidateQueries({ queryKey: saveProposalKeys.bySession(sessionId) });
  // FRONT-2386: the `/ended` route can mint/title this chat's thread (a
  // voice-only call never otherwise gets one) — without this, the top-bar
  // chat nav and the Chats page keep a stale title until something else
  // refetches the list.
  queryClient.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
}
